import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Touchable } from './touchable';
import {
  getRecoveryNotice,
  subscribeRecoveryNotice,
  clearRecoveryNotice,
  type RecoveryKind,
} from '../storage/recoveryNotice';

const AUTO_DISMISS_MS = 8000;

/**
 * One-time toast shown when boot recovery restored data the primary blob had
 * lost (session mirror or backup generation). Deliberately loud: the user
 * should know the safety net fired — if they see this often, something is
 * wrong and they should back up + report it.
 */
export default function RecoveryToast() {
  const { t } = useTranslation('app');
  const [kind, setKind] = useState<RecoveryKind | null>(getRecoveryNotice);

  useEffect(() => subscribeRecoveryNotice(() => setKind(getRecoveryNotice())), []);

  useEffect(() => {
    if (!kind) return;
    const id = setTimeout(() => clearRecoveryNotice(), AUTO_DISMISS_MS);
    return () => clearTimeout(id);
  }, [kind]);

  if (!kind) return null;

  const message =
    kind === 'mirror'
      ? t('recovery.mirror', 'Recovered unsaved knitting progress from your last visit.')
      : t('recovery.backup', 'Restored your data from the local backup copy.');

  return (
    <div
      role="status"
      className="fixed top-2 left-1/2 z-[70] flex w-[min(92vw,28rem)] -translate-x-1/2 items-center justify-between gap-3 rounded-xl border border-success bg-success-soft p-3 shadow-lg"
    >
      <p className="text-sm font-semibold text-ink">{message}</p>
      <Touchable
        haptic="softTap"
        onClick={clearRecoveryNotice}
        aria-label={t('recovery.dismiss', 'Dismiss')}
        className="shrink-0 rounded-full px-2 py-1 text-sm font-semibold text-ink-muted"
      >
        ✕
      </Touchable>
    </div>
  );
}
